export interface CoffeeIdentity {
  id?: string;
  serviceId?: string;
  title?: string;
  localTitle?: string;
}

const identityIds = (coffee: CoffeeIdentity): string[] =>
  [coffee.id, coffee.serviceId].filter((id): id is string => Boolean(id)).map((id) => id.toLowerCase());

const identityTitles = (coffee: CoffeeIdentity): string[] =>
  [coffee.title, coffee.localTitle].map((title) => title?.trim().toLowerCase()).filter((title): title is string => Boolean(title));

/**
 * Two records are the same coffee when they share a product id (id or
 * serviceId) or the same normalised title. Titles are the only link between
 * records that share no key (the app forbids two coffees sharing a title), but
 * the looser `localId` used for the scale markers is deliberately not used for
 * locking, so unrelated coffees can never cross-lock.
 */
export const isSameCoffee = (left: CoffeeIdentity, right: CoffeeIdentity): boolean => {
  const rightIds = identityIds(right);
  const rightTitles = identityTitles(right);
  return identityIds(left).some((id) => rightIds.includes(id)) || identityTitles(left).some((title) => rightTitles.includes(title));
};

/**
 * Whether the coffee already exists in PIM, scored or not. Intensity is set
 * once per coffee, so such a coffee's intensity is locked. Matched on product
 * id or title only, like sibling locking, so unrelated coffees never cross-lock.
 */
export const isCoffeeOnPim = (coffee: CoffeeIdentity | undefined, pimProducts: readonly CoffeeIdentity[]): boolean =>
  coffee !== undefined && pimProducts.some((pim) => isSameCoffee(pim, coffee));

/**
 * Whether PIM holds this coffee under a shared product id (id or serviceId),
 * as opposed to only a matching title. A title is editable, so only an id
 * match proves the coffee is still the PIM one after a rename.
 */
export const isCoffeeIdentifiedInPimById = (coffee: CoffeeIdentity | undefined, pimProducts: readonly CoffeeIdentity[]): boolean => {
  if (coffee === undefined) return false;
  const ids = identityIds(coffee);
  return pimProducts.some((pim) => identityIds(pim).some((id) => ids.includes(id)));
};
